import { Error as ErrorIcon } from '@mui/icons-material';
import { Box, Skeleton, Stack, type SxProps, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useId } from 'react';

import { campaignManagerTheme } from '@campaign-manager/theme';

import FormInputRowLabel from '../FormInputRowLabel';
import Value from './Value';

interface Props {
  label?: string | ReactNode;
  tooltipText?: string | ReactNode;
  value: string | number | boolean | ReactNode;
  href?: string;
  loading?: boolean;
  error?: Error;
  sx?: SxProps;
  valueContainerSx?: SxProps;
  labelAlignItems?: 'flex-start' | 'center' | 'flex-end';
}

export default function FormInputRowReadOnly({
  label,
  value,
  href,
  loading,
  error,
  sx,
  valueContainerSx,
  tooltipText,
  labelAlignItems,
}: Props) {
  const labelId = useId();
  const valueId = useId();
  const errorId = useId();
  const tooltipId = useId();

  const valueComp = loading ? (
    <Skeleton
      animation="wave"
      width="100%"
      aria-label="Loading..."
    />
  ) : (
    <Value
      value={value}
      href={href}
    />
  );

  return (
    <Stack
      role="group"
      aria-labelledby={labelId}
      data-testid="form-input-row-readonly"
      sx={{
        columnGap: 4,
        alignItems: 'start',
        flexDirection: { xs: 'column', lg: 'row' },
        ...sx,
      }}
    >
      <Stack
        component="dt"
        direction="row"
        sx={{
          alignItems: labelAlignItems ? labelAlignItems : 'center',
          flex: { xs: '1 1 auto', md: '0 0 30%' },
        }}
      >
        <FormInputRowLabel
          label={label}
          tooltipText={tooltipText}
          id={labelId}
          tooltipId={tooltipId}
        />
      </Stack>
      <Stack
        component="dd"
        direction="row"
        aria-describedby={tooltipText ? tooltipId : undefined}
        sx={{
          alignItems: 'flex-start',
          justifyContent: 'stretch',
          flex: '1 1 auto',
          margin: 0,
          minWidth: 0,
          overflow: 'auto',
          ...valueContainerSx,
        }}
      >
        {error ? (
          <Stack
            direction="row"
            role="alert"
            aria-live="polite"
            id={errorId}
            sx={{
              alignItems: 'center',
              gap: 2,
            }}
          >
            <ErrorIcon
              color="error"
              fontSize="small"
              aria-hidden="true"
            />
            <Typography
              sx={{
                color: campaignManagerTheme.color.error,
                fontSize: 'small',
              }}
            >
              {`${error.message}`}
            </Typography>
          </Stack>
        ) : (
          <Box
            component="div"
            sx={{
              flex: '1 1 auto',
              minHeight: '32px',
              lineHeight: '24px',
            }}
          >
            {valueComp}
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
